import { useMemo } from 'react';
import { Icon } from '../../components/ui/Icon';
import { moduleDataStatus } from '../../config/systems';
import { ROLES, PERMS, SUITES } from '../../data/mocks/platform';
import type { PermLevel } from '../../data/types/platform';

export interface RoleDataPreviewProps {
  /** Currently-considered role id. */
  roleId: string;
  /** Optional region scoping. `['all']` (or empty) means no district narrowing. */
  regions?: string[];
  /** Tighter layout for embedding inside drawers. */
  dense?: boolean;
}

interface SensitiveAction {
  he: string;
  /**
   * Roles allowed to perform the action. Either an explicit set of role ids
   * or a predicate over the role row.
   */
  roles: string[];
}

/**
 * Static lookup of sensitive actions per role. These are display-only —
 * any real entitlement check happens server-side. We surface them here so
 * an admin assigning a role can see the implicit privileges that come
 * with it (PII exports, audit access, etc.).
 */
const SENSITIVE_ACTIONS: SensitiveAction[] = [
  { he: 'ייצוא רשימת GOTV (PII)',       roles: ['platform-admin', 'head'] },
  { he: 'ייצוא רשימת משתתפים מלאה',     roles: ['platform-admin', 'head', 'ops'] },
  { he: 'צפייה ביומן פעולות אדמין',     roles: ['platform-admin'] },
  { he: 'שינוי תפקידים והרשאות',        roles: ['platform-admin'] },
  { he: 'הזמנת משתמשים חדשים',           roles: ['platform-admin', 'head'] },
  { he: 'שליפת קונטקטים מאזורים אחרים', roles: ['platform-admin', 'head', 'exec'] },
];

function lvlLabel(lvl: PermLevel | null | undefined): string {
  if (lvl === 'admin') return 'ניהול';
  if (lvl === 'edit')  return 'עריכה';
  if (lvl === 'view')  return 'צפייה';
  return '—';
}

function lvlIconName(lvl: PermLevel | null | undefined): string | undefined {
  if (lvl === 'admin') return 'shield-check';
  if (lvl === 'edit')  return 'pencil';
  if (lvl === 'view')  return 'eye';
  return undefined;
}

export function RoleDataPreview({
  roleId,
  regions,
  dense = false,
}: RoleDataPreviewProps) {
  const role = ROLES.find((r) => r.id === roleId);
  const rolePerms = PERMS[roleId] ?? {};

  const { systemsWithAccess, totalModules } = useMemo(() => {
    const accessibleSuites = SUITES.filter((s) => rolePerms[s.id]);
    const modCount = accessibleSuites.reduce((sum, s) => sum + s.modules.length, 0);
    return { systemsWithAccess: accessibleSuites, totalModules: modCount };
  }, [rolePerms]);

  const regionList = regions ?? [];
  const isAllRegions =
    regionList.length === 0 ||
    regionList.includes('all') ||
    role?.scope === 'all' ||
    role?.scope === 'view-all';
  const regionsCount = isAllRegions ? '—' : String(regionList.length);

  const sensitive = SENSITIVE_ACTIONS.filter((a) => a.roles.includes(roleId));

  if (!role) {
    return (
      <div className={'role-preview' + (dense ? ' dense' : '')}>
        <div className="rdp-empty">תפקיד לא ידוע</div>
      </div>
    );
  }

  return (
    <div className={'role-preview' + (dense ? ' dense' : '')}>
      {/* Overview */}
      <div className="rdp-overview">
        <span className={'role-pill role-' + role.id}>
          <span className="role-pill-short">{role.short}</span>
          {role.he}
        </span>
        <div className="rdp-overview-text">
          תפקיד <b>{role.he}</b> ייתן גישה ל-
          <span className="rdp-num">{systemsWithAccess.length}</span> מערכות ·{' '}
          <span className="rdp-num">{totalModules}</span> מודולים ·{' '}
          <span className="rdp-num">{regionsCount === '—' ? 'כל ה' : regionsCount + ' '}</span>
          {regionsCount === '—' ? 'מחוזות' : 'מחוזות'}
        </div>
      </div>

      {/* Per-system cards */}
      {systemsWithAccess.length === 0 ? (
        <div className="rdp-empty">לתפקיד זה אין גישה לאף מערכת.</div>
      ) : (
        <div className="rdp-systems">
          {systemsWithAccess.map((s) => {
            const lvl = rolePerms[s.id] as PermLevel;
            const iconName = lvlIconName(lvl);
            return (
              <div key={s.id} className={'rdp-system lvl-' + lvl}>
                <div className="rdp-system-head">
                  <span className="rdp-system-name">
                    <Icon name={s.icon} size={14} />
                    {s.he}
                  </span>
                  <span className={'rdp-lvl-pill lvl-' + lvl}>
                    {iconName && <Icon name={iconName} size={11} />}
                    {lvlLabel(lvl)}
                  </span>
                </div>
                <div className="rdp-modules">
                  {s.modules.map((m) => {
                    const status = moduleDataStatus(s.id, m.id);
                    return (
                      <span
                        key={m.id}
                        className={'rdp-module-chip status-' + status}
                        title={`${m.he} · ${status === 'live' ? 'נתונים חיים' : 'נתוני דמו'}`}
                      >
                        <span className={'rdp-dot dot-' + status} />
                        <span className="rdp-module-name">{m.he}</span>
                        {status === 'mock' && (
                          <span className="rdp-module-mock">(נתוני דמו)</span>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Systems with no access — render muted */}
          {SUITES.filter((s) => !rolePerms[s.id]).map((s) => (
            <div key={s.id} className="rdp-system lvl-none">
              <div className="rdp-system-head">
                <span className="rdp-system-name muted">
                  <Icon name={s.icon} size={14} />
                  {s.he}
                </span>
                <span className="rdp-lvl-pill lvl-none">— אין גישה</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Regional scope */}
      <div className="rdp-section">
        <div className="rdp-section-head">היקף מחוזי</div>
        <div className="rdp-regions">
          {isAllRegions ? (
            <span className="rdp-region-chip all">
              <Icon name="globe" size={11} /> כל המחוזות
            </span>
          ) : regionList.length === 0 ? (
            <span className="rdp-region-chip muted">לא הוגדר</span>
          ) : (
            regionList.map((r) => (
              <span key={r} className="rdp-region-chip">
                <Icon name="map-pin" size={11} />
                {r}
              </span>
            ))
          )}
        </div>
      </div>

      {/* Sensitive actions */}
      {sensitive.length > 0 && (
        <div className="rdp-section">
          <div className="rdp-section-head">
            <Icon name="shield-alert" size={12} /> פעולות רגישות בתפקיד
          </div>
          <ul className="rdp-sensitive">
            {sensitive.map((a) => (
              <li key={a.he}>
                <Icon name="check" size={11} />
                {a.he}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
